import { USER_COLORS } from '@doriavboard/shared';
import { Check, Monitor, Moon, Sun } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { errorMessage } from '../api/client';
import { authApi } from '../api/endpoints';
import { cn } from '../lib/cn';
import { accountLabel } from '../lib/users';
import { useSession } from '../stores/session';
import { type ThemePreference, useTheme } from '../stores/theme';
import { toast } from '../stores/toasts';
import { Avatar } from './ui/Avatar';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Modal } from './ui/Modal';

const THEMES: { value: ThemePreference; label: string; icon: React.ReactNode }[] = [
  { value: 'light', label: 'Light', icon: <Sun className="size-4" /> },
  { value: 'dark', label: 'Dark', icon: <Moon className="size-4" /> },
  { value: 'system', label: 'System', icon: <Monitor className="size-4" /> },
];

export function AccountDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
}) {
  const user = useSession((s) => s.user);
  const setUser = useSession((s) => s.setUser);
  const { preference, setPreference } = useTheme();
  const [name, setName] = useState(user?.name ?? '');
  const [color, setColor] = useState(user?.color ?? USER_COLORS[0]);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [username, setUsername] = useState('');
  const [claimError, setClaimError] = useState<string | null>(null);

  useEffect(() => {
    if (open && user) {
      setName(user.name);
      setColor(user.color);
      setCurrent('');
      setNext('');
      setPasswordError(null);
      setUsername('');
      setClaimError(null);
    }
  }, [open, user]);

  if (!user) return null;

  async function saveProfile(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      const { user: updated } = await authApi.updateProfile({ name, color });
      setUser(updated);
      toast('Profile saved', 'success');
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  }

  async function changePassword(event: FormEvent) {
    event.preventDefault();
    setPasswordError(null);
    try {
      await authApi.changePassword(current, next);
      setCurrent('');
      setNext('');
      toast('Password changed. Other devices were signed out.', 'success');
    } catch (err) {
      setPasswordError(errorMessage(err));
    }
  }

  async function claim(event: FormEvent) {
    event.preventDefault();
    setClaimError(null);
    try {
      const { user: saved } = await authApi.claim(username, next);
      setUser(saved);
      setNext('');
      toast(`Saved. Sign in as ${saved.username} from any device.`, 'success');
    } catch (err) {
      setClaimError(errorMessage(err));
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Account settings"
      description={accountLabel(user)}
    >
      <form onSubmit={saveProfile} className="flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <Avatar name={name || user.name} color={color} size="lg" />
          <div className="flex-1">
            <Input
              label="Display name"
              value={name}
              maxLength={60}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
        </div>
        <fieldset>
          <legend className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
            Cursor color
          </legend>
          <div className="flex flex-wrap gap-2">
            {USER_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                className="focus-ring grid size-8 place-items-center rounded-full"
                style={{ backgroundColor: c }}
                aria-label={`Use color ${c}`}
                aria-pressed={c === color}
              >
                {c === color && <Check className="size-4 text-white" />}
              </button>
            ))}
          </div>
        </fieldset>
        <div className="flex justify-end">
          <Button type="submit" variant="primary" loading={saving}>
            Save profile
          </Button>
        </div>
      </form>

      <hr className="my-6 border-slate-200 dark:border-slate-800" />

      <h3 className="mb-3 text-sm font-medium text-slate-700 dark:text-slate-300">Appearance</h3>
      <div className="mb-6 grid grid-cols-3 gap-2">
        {THEMES.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => setPreference(t.value)}
            className={cn(
              'focus-ring flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm',
              preference === t.value
                ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-950/40 dark:text-brand-300'
                : 'border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800',
            )}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      <hr className="mb-6 border-slate-200 dark:border-slate-800" />

      {user.isGuest ? (
        <form onSubmit={claim} className="flex flex-col gap-3">
          <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Save your account
          </h3>
          <p className="-mt-1 text-sm text-slate-500">
            You’re a guest: your boards live in this browser’s session. Choose a username and
            password to sign in from anywhere and keep them.
          </p>
          <Input
            placeholder="Username"
            aria-label="Username"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            minLength={3}
            maxLength={30}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
          <Input
            type="password"
            placeholder="Password (8+ characters)"
            aria-label="Password"
            autoComplete="new-password"
            minLength={8}
            value={next}
            onChange={(e) => setNext(e.target.value)}
            error={claimError}
            required
          />
          <div className="flex justify-end">
            <Button type="submit" variant="primary">
              Save account
            </Button>
          </div>
        </form>
      ) : (
        <form onSubmit={changePassword} className="flex flex-col gap-3">
          <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Change password
          </h3>
          <Input
            type="password"
            placeholder="Current password"
            autoComplete="current-password"
            aria-label="Current password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
          />
          <Input
            type="password"
            placeholder="New password (8+ characters)"
            autoComplete="new-password"
            aria-label="New password"
            minLength={8}
            value={next}
            onChange={(e) => setNext(e.target.value)}
            error={passwordError}
            required
          />
          <div className="flex justify-end">
            <Button type="submit">Change password</Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
